/**
 * Data-fetching hooks for Motivation screen
 * Each hook fetches its own data independently for use with Suspense
 */
import { useQuery } from '@tanstack/react-query';
import { useBackend } from '../../../Hooks/useBackend';
import { useLanguage } from '../../../Hooks/useLanguage';
import { RouteChallenge, RouteChallengeApi } from '../../../Model/Engagement/RouteChallenge/RouteChallenge';
import { IndividualChallenge, IndividualChallengeApi } from '../../../Model/Engagement/IndividualChallenge/IndividualChallenge';
import { CommunityWallPost, CommunityWallPostApi } from '../../../Model/Engagement/CommunityWall/CommunityWallPost';
import { MyImpactApi } from '../../../Model/Engagement/Impact/MyImpact';
import { PointsModel, PointsModelApi } from '../../../Model/Engagement/Rewards/PointsModel';

export function useCurrentChallenges() {
  return useQuery({
    queryKey: ['motivation', 'challenges', 'current'],
    queryFn: async () => {
      const [route, individual] = await Promise.all([
        RouteChallengeApi.current().catch(() => []),
        IndividualChallengeApi.current().catch(() => []),
      ]);
      return {
        routeChallenges: route ?? [],
        individualChallenges: individual ?? [],
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useMyChallenges() {
  return useQuery({
    queryKey: ['motivation', 'challenges', 'mine'],
    queryFn: async () => {
      const [route, individual] = await Promise.all([
        RouteChallengeApi.myChallenges({ size: 10 }).catch(() => []),
        IndividualChallengeApi.myChallenges({ size: 10 }).catch(() => []),
      ]);
      return {
        pastRouteChallenges: route ?? [],
        pastIndividualChallenges: individual ?? [],
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCommunityPosts(size: number = 12) {
  return useQuery({
    queryKey: ['motivation', 'community', 'posts', size],
    queryFn: async () => {
      const posts = await CommunityWallPostApi.find({ size, sort: 'createdAt,desc' }).catch(() => []);
      return posts ?? [];
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useImpactSummaries() {
  const { language } = useLanguage();

  return useQuery({
    queryKey: ['motivation', 'impact', 'summaries', language],
    queryFn: async () => {
      const summaries = await MyImpactApi.getTemplateSummaries(language).catch(() => []);
      return summaries ?? [];
    },
    staleTime: 10 * 60 * 1000,
  });
}

export function usePointsOverview() {
  return useQuery({
    queryKey: ['motivation', 'points', 'overview'],
    queryFn: async () => {
      const points = await PointsModelApi.overview().catch(() => undefined);
      return points;
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}
